// SPDX-License-Identifier: AGPL-3.0-or-later

package kairos

import kotlin.time.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable

@Serializable
data class KairosDailyPlan(
    val date: LocalDate,
    val userId: Kairos.UserId?,
    val projectId: Kairos.ProjectId?,
    val inbox: List<Kairos.Ticket>,
    val overdue: List<Kairos.Ticket>,
    val dueToday: List<Kairos.Ticket>,
    val upcoming: List<Kairos.Ticket>,
    val activeWithoutDeadline: List<Kairos.Ticket>,
    val eventsToday: List<Kairos.Event>,
    val timeboxesToday: List<Kairos.Timebox>,
    val routines: List<Kairos.Routine>,
    val objectivePressure: List<KairosObjectivePressure>,
)

@Serializable
data class KairosExecutionFlow(
    val date: LocalDate,
    val userId: Kairos.UserId?,
    val projectId: Kairos.ProjectId?,
    val now: List<Kairos.Ticket>,
    val inFlight: List<Kairos.Ticket>,
    val next: List<Kairos.Ticket>,
    val later: List<Kairos.Ticket>,
    val inbox: List<Kairos.Ticket>,
)

@Serializable
data class KairosObjectivePressure(
    val objective: Kairos.Objective,
    val openTicketCount: Int,
    val completedTicketCount: Int,
    val missingMeasurementCount: Int,
    val offTargetKpiCount: Int,
)

fun currentKairosDate(timeZone: TimeZone = TimeZone.currentSystemDefault()): LocalDate =
    Clock.System.now().toLocalDateTime(timeZone).date

fun buildKairosDailyPlan(
    workspace: KairosWorkspaceSnapshot,
    today: LocalDate,
    userId: Kairos.UserId? = null,
    projectId: Kairos.ProjectId? = null,
    timeZone: TimeZone = TimeZone.currentSystemDefault(),
    upcomingWindowDays: Int = 7,
    objectiveWindowDays: Int = 14,
): KairosDailyPlan {
    require(upcomingWindowDays >= 0) { "upcomingWindowDays must not be negative" }
    require(objectiveWindowDays >= 0) { "objectiveWindowDays must not be negative" }

    val scopedUserIdValue = userId?.value
    val projectIds =
        projectId?.let { scopedProjectId ->
            projectAndDescendantIds(scopedProjectId, workspace.projects)
        }
    val scopedTickets =
        workspace.tickets.filter { ticket ->
            val userMatches =
                scopedUserIdValue == null || ticket.assigneeUserId?.value == scopedUserIdValue
            val projectMatches = projectIds == null || ticket.projectId in projectIds
            userMatches && projectMatches
        }
    val openScopedTickets = scopedTickets.filter { ticket -> ticket.completedAt == null }
    val scopedTicketIds = scopedTickets.mapTo(mutableSetOf()) { ticket -> ticket.id }
    val relevantProjectIds =
        when {
            projectIds != null -> projectIds
            scopedTickets.isNotEmpty() ->
                scopedTickets.mapTo(mutableSetOf()) { ticket -> ticket.projectId }
            userId == null -> workspace.projects.mapTo(mutableSetOf()) { project -> project.id }
            else -> emptySet()
        }

    val inbox = mutableListOf<Kairos.Ticket>()
    val overdue = mutableListOf<Kairos.Ticket>()
    val dueToday = mutableListOf<Kairos.Ticket>()
    val upcoming = mutableListOf<Kairos.Ticket>()
    val activeWithoutDeadline = mutableListOf<Kairos.Ticket>()
    val upcomingCutoff = today + DatePeriod(days = upcomingWindowDays)

    openScopedTickets.forEach { ticket ->
        if (ticket.stage == Kairos.TicketStage.Inbox) {
            inbox += ticket
            return@forEach
        }
        val deadlineDate = ticket.deadlineDate()
        when {
            deadlineDate == null &&
                ticket.stage in
                    setOf(
                        Kairos.TicketStage.InProgress,
                        Kairos.TicketStage.Review,
                        Kairos.TicketStage.Blocked,
                    ) -> activeWithoutDeadline += ticket
            deadlineDate == null -> {}
            deadlineDate < today -> overdue += ticket
            deadlineDate == today -> dueToday += ticket
            deadlineDate <= upcomingCutoff -> upcoming += ticket
        }
    }

    val ticketOrdering = compareBy<Kairos.Ticket>({ it.deadline?.toInstant() }, { it.createdAt })
    val ticketsById = workspace.tickets.associateBy { ticket -> ticket.id }
    val latestMeasurementsByKpi =
        workspace.measurements
            .groupBy { measurement -> measurement.kpiId }
            .mapValues { (_, measurements) ->
                measurements.maxByOrNull { measurement -> measurement.recordedAt }
            }

    val eventsToday =
        workspace.events
            .filter { event ->
                eventMatchesScope(event = event, relevantProjectIds = relevantProjectIds) &&
                    event.spansDate(today)
            }
            .sortedBy { event -> event.dtStart.toInstant() }

    val timeboxesToday =
        workspace.timeboxes
            .filter { timebox ->
                timeboxMatchesScope(
                    timebox = timebox,
                    relevantProjectIds = relevantProjectIds,
                    scopedTicketIds = scopedTicketIds,
                ) && timebox.startDate(timeZone) == today
            }
            .sortedBy { timebox -> timebox.startTime }

    val routines =
        workspace.routines
            .filter { routine ->
                routineMatchesScope(routine = routine, relevantProjectIds = relevantProjectIds)
            }
            .sortedWith(
                compareBy<Kairos.Routine>(
                    { routine -> routine.projectId?.value.orEmpty() },
                    { routine -> routine.description.lowercase() },
                )
            )

    val objectiveDeadlineCutoff = today + DatePeriod(days = objectiveWindowDays)
    val objectivePressure =
        workspace.objectives
            .mapNotNull { objective ->
                val linkedTickets =
                    objective.tickets
                        .mapNotNull { ticketId -> ticketsById[ticketId] }
                        .filter { ticket ->
                            scopedUserIdValue == null ||
                                ticket.assigneeUserId?.value == scopedUserIdValue
                        }
                val projectMatches =
                    projectIds == null ||
                        objective.projectId == null ||
                        objective.projectId in projectIds
                val relatedToScopedTickets =
                    linkedTickets.any { ticket -> ticket.id in scopedTicketIds }
                val relatedToRelevantProject =
                    objective.projectId != null && objective.projectId in relevantProjectIds
                if (!projectMatches && !relatedToScopedTickets && !relatedToRelevantProject) {
                    return@mapNotNull null
                }

                val openTicketCount = linkedTickets.count { ticket -> ticket.completedAt == null }
                val completedTicketCount = linkedTickets.size - openTicketCount
                val measurementSignals =
                    objective.kpiTargets.map { target ->
                        latestMeasurementsByKpi[target.kpi]?.value
                    }
                val missingMeasurementCount =
                    measurementSignals.count { measurement -> measurement == null }
                val offTargetKpiCount =
                    objective.kpiTargets.count { target ->
                        val latestValue = latestMeasurementsByKpi[target.kpi]?.value
                        latestValue != null && !target.target.isMetBy(latestValue)
                    }
                val deadlineDate = objective.deadlineDate()
                val deadlinePressure =
                    deadlineDate != null &&
                        deadlineDate <= objectiveDeadlineCutoff &&
                        (openTicketCount > 0 || objective.kpiTargets.isNotEmpty())
                val measurementPressure =
                    offTargetKpiCount > 0 ||
                        (missingMeasurementCount > 0 &&
                            deadlineDate != null &&
                            deadlineDate <= objectiveDeadlineCutoff)
                if (!deadlinePressure && !measurementPressure) {
                    return@mapNotNull null
                }
                KairosObjectivePressure(
                    objective = objective,
                    openTicketCount = openTicketCount,
                    completedTicketCount = completedTicketCount,
                    missingMeasurementCount = missingMeasurementCount,
                    offTargetKpiCount = offTargetKpiCount,
                )
            }
            .sortedWith(
                compareBy<KairosObjectivePressure>(
                    { pressure -> pressure.objective.deadline?.toInstant() },
                    { pressure -> -pressure.offTargetKpiCount },
                    { pressure -> -pressure.openTicketCount },
                    { pressure -> pressure.objective.createdAt },
                )
            )

    return KairosDailyPlan(
        date = today,
        userId = userId,
        projectId = projectId,
        inbox = inbox.sortedWith(ticketOrdering),
        overdue = overdue.sortedWith(ticketOrdering),
        dueToday = dueToday.sortedWith(ticketOrdering),
        upcoming = upcoming.sortedWith(ticketOrdering),
        activeWithoutDeadline = activeWithoutDeadline.sortedBy { ticket -> ticket.createdAt },
        eventsToday = eventsToday,
        timeboxesToday = timeboxesToday,
        routines = routines,
        objectivePressure = objectivePressure,
    )
}

fun buildKairosExecutionFlow(
    workspace: KairosWorkspaceSnapshot,
    dailyPlan: KairosDailyPlan,
): KairosExecutionFlow {
    val projectIds =
        dailyPlan.projectId?.let { scopedProjectId ->
            projectAndDescendantIds(scopedProjectId, workspace.projects)
        }
    val scopedOpenTickets =
        workspace.tickets.filter { ticket ->
            val userMatches = dailyPlan.userId == null || ticket.assigneeUserId == dailyPlan.userId
            val projectMatches = projectIds == null || ticket.projectId in projectIds
            userMatches && projectMatches && ticket.completedAt == null
        }
    val ticketOrdering = compareBy<Kairos.Ticket>({ it.deadline?.toInstant() }, { it.createdAt })

    val now = (dailyPlan.overdue + dailyPlan.dueToday).distinctBy { ticket -> ticket.id }
    val nowIds = now.mapTo(mutableSetOf()) { ticket -> ticket.id }
    val inboxIds = dailyPlan.inbox.mapTo(mutableSetOf()) { ticket -> ticket.id }
    val inFlight =
        scopedOpenTickets
            .filter { ticket ->
                ticket.stage in
                    setOf(
                        Kairos.TicketStage.InProgress,
                        Kairos.TicketStage.Review,
                        Kairos.TicketStage.Blocked,
                    ) && ticket.id !in nowIds && ticket.id !in inboxIds
            }
            .sortedWith(ticketOrdering)
    val inFlightIds = inFlight.mapTo(mutableSetOf()) { ticket -> ticket.id }
    val next =
        dailyPlan.upcoming
            .filter { ticket ->
                ticket.id !in nowIds && ticket.id !in inboxIds && ticket.id !in inFlightIds
            }
            .sortedWith(ticketOrdering)
    val nextIds = next.mapTo(mutableSetOf()) { ticket -> ticket.id }
    val surfacedIds = nowIds + inboxIds + inFlightIds + nextIds
    val later =
        scopedOpenTickets.filter { ticket -> ticket.id !in surfacedIds }.sortedWith(ticketOrdering)

    return KairosExecutionFlow(
        date = dailyPlan.date,
        userId = dailyPlan.userId,
        projectId = dailyPlan.projectId,
        now = now.sortedWith(ticketOrdering),
        inFlight = inFlight,
        next = next,
        later = later,
        inbox = dailyPlan.inbox.sortedWith(ticketOrdering),
    )
}

fun projectAndDescendantIds(
    rootProjectId: Kairos.ProjectId,
    projects: List<Kairos.Project>,
): Set<Kairos.ProjectId> {
    val childrenByParent =
        projects
            .filter { project -> project.parentProjectId != null }
            .groupBy { project -> requireNotNull(project.parentProjectId) }
    val result = mutableSetOf<Kairos.ProjectId>()
    val queue = ArrayDeque<Kairos.ProjectId>()
    queue += rootProjectId
    while (queue.isNotEmpty()) {
        val projectId = queue.removeFirst()
        if (!result.add(projectId)) {
            continue
        }
        childrenByParent[projectId].orEmpty().forEach { child -> queue += child.id }
    }
    return result
}

private fun Kairos.Ticket.deadlineDate(): LocalDate? = deadline?.dateTime?.date

private fun Kairos.Objective.deadlineDate(): LocalDate? = deadline?.dateTime?.date

private fun Kairos.Event.spansDate(date: LocalDate): Boolean {
    val startDate = dtStart.dateTime.date
    val endDate = (dtEnd ?: dtStart).dateTime.date
    return date >= startDate && date <= endDate
}

private fun Kairos.Timebox.startDate(timeZone: TimeZone): LocalDate =
    startTime.toLocalDateTime(timeZone).date

private fun Kairos.KpiTarget.isMetBy(value: Double): Boolean =
    when (this) {
        is Kairos.KpiTarget.AtLeast -> value >= this.value
        is Kairos.KpiTarget.AtMost -> value <= this.value
        is Kairos.KpiTarget.Range -> value in this.min..this.max
    }

private fun eventMatchesScope(
    event: Kairos.Event,
    relevantProjectIds: Set<Kairos.ProjectId>,
): Boolean =
    when {
        relevantProjectIds.isEmpty() -> false
        event.projectId == null -> false
        else -> event.projectId in relevantProjectIds
    }

private fun timeboxMatchesScope(
    timebox: Kairos.Timebox,
    relevantProjectIds: Set<Kairos.ProjectId>,
    scopedTicketIds: Set<Kairos.TicketId>,
): Boolean =
    timebox.tickets.any { ticketId -> ticketId in scopedTicketIds } ||
        (timebox.projectId != null && timebox.projectId in relevantProjectIds)

private fun routineMatchesScope(
    routine: Kairos.Routine,
    relevantProjectIds: Set<Kairos.ProjectId>,
): Boolean =
    when {
        relevantProjectIds.isEmpty() -> false
        routine.projectId == null -> false
        else -> routine.projectId in relevantProjectIds
    }
